/**
 * Achievements/badges data layer.
 *
 * Shapes mirror the `/user/v1/badges` API response 1:1 (snake_case), so the
 * axios response needs no remapping. The API returns:
 *   - `eligible_badges`: badges the student's grade + product can earn.
 *   - `awarded_badges`:  badges the student has actually received (with counts,
 *     the module they were earned in, and timestamps).
 *
 * The API does NOT send images (`icon_url` is empty), so artwork stays local in
 * `BADGE_IMAGES`, keyed by the snake_case `badge_id`. The backend grade-filters
 * `eligible_badges` per student, so no client-side grade filter is needed.
 *
 * Add a new badge:
 *   1. Drop the .webp into ./assets/ and import it below.
 *   2. Add it to BADGE_IMAGES (keyed by badge_id).
 */
import bigLeap from './assets/big_leap.webp';
import bookBoss from './assets/book_boss.webp';
import braveBear from './assets/brave_bear.webp';
import bridgeBuilder from './assets/bridge_builder.webp';
import busyBee from './assets/busy_bee.webp';
import clearVoiceCaptain from './assets/clear_voice_captain.webp';
import confidenceCaptain from './assets/confidence_captain.webp';
import confidenceCommunicator from './assets/confidence_communicator.webp';
import criticalThinker from './assets/critical_thinker.webp';
import curiosityCatalyst from './assets/curiosity_catalyst.webp';
import curiousCheetah from './assets/curious_cheetah.webp';
import discussionBuilder from './assets/discussion_builder.webp';
import engagementChampion from './assets/engagement_champion.webp';
import expressionExpert from './assets/expression_expert.webp';
import fearlessFalcon from './assets/fearless_falcon.webp';
import frame1 from './assets/Frame 1.webp';
import frame10 from './assets/Frame 10.webp';
import frame11 from './assets/Frame 11.webp';
import frame12 from './assets/Frame 12.webp';
import frame13 from './assets/Frame 13.webp';
import frame14 from './assets/Frame 14.webp';
import frame15 from './assets/Frame 15.webp';
import frame17 from './assets/Frame 17.webp';
import frame18 from './assets/Frame 18.webp';
import frame2 from './assets/Frame 2.webp';
import frame20 from './assets/Frame 20.webp';
import frame21 from './assets/Frame 21.webp';
import frame23 from './assets/Frame 23.webp';
import frame24 from './assets/Frame 24.webp';
import frame25 from './assets/Frame 25.webp';
import frame26 from './assets/Frame 26.webp';
import frame27 from './assets/Frame 27.webp';
import frame28 from './assets/Frame 28.webp';
import frame29 from './assets/Frame 29.webp';
import frame3 from './assets/Frame 3.webp';
import frame30 from './assets/Frame 30.webp';
import frame31 from './assets/Frame 31.webp';
import frame32 from './assets/Frame 32.webp';
import frame33 from './assets/Frame 33.webp';
import frame34 from './assets/Frame 34.webp';
import frame4 from './assets/Frame 4.webp';
import frame5 from './assets/Frame 5.webp';
import frame6 from './assets/Frame 6.webp';
import frame7 from './assets/Frame 7.webp';
import frame8 from './assets/Frame 8.webp';
import frame9 from './assets/Frame 9.webp';
import goGetter from './assets/go_getter.webp';
import homeworkHero from './assets/homework_hero.webp';
import ideaInventor from './assets/idea_inventor.webp';
import insightInvestigator from './assets/insight_investigator.webp';
import kindHeart from './assets/kind_heart.webp';
import knowledgeContributor from './assets/knowledge_contributor.webp';
import leadershipCatalyst from './assets/leadership_catalyst.webp';
import listeningLion from './assets/listening_lion.webp';
import moduleAttendanceStar from './assets/module_attendance_star.webp';
import moduleExpert from './assets/module_expert.webp';
import moduleQuizMaster from './assets/module_quiz_master.webp';
import moduleQuizStar from './assets/module_quiz_star.webp';
import moduleQuizStriker from './assets/module_quiz_striker.webp';
import perspectiveShifter from './assets/perspective_shifter.webp';
import powerParticipant from './assets/power_participant.webp';
import practiceChampion from './assets/practice_champion.webp';
import practiceLegend from './assets/practice_legend.webp';
import practiceStar from './assets/practice_star.webp';
import questionArchitect from './assets/question_architect.webp';
import quizChampion from './assets/quiz_champion.webp';
import quizLegend from './assets/quiz_legend.webp';
import readingRanger from './assets/reading_ranger.webp';
import reasoningRanger from './assets/reasoning_ranger.webp';
import resilienceRockstar from './assets/resilience_rockstar.webp';
import risingRocket from './assets/rising_rocket.webp';
import sentenceSuperstar from './assets/sentence_superstar.webp';
import solutionStrategist from './assets/solution_strategist.webp';
import soundHero from './assets/sound_hero.webp';
import soundScout from './assets/sound_scout.webp';
import vocabularyVirtuoso from './assets/vocabulary_virtuoso.webp';
import wordWarrior from './assets/word_warrior.webp';
import wordWizard from './assets/word_wizard.webp';

/* ----------------------------- Wire types ------------------------------- */
/** auto = system-given, manual = professor/trainer-given. */
export type BadgeType = 'auto' | 'manual';

/** A badge the student's grade + product is eligible to earn. */
export interface EligibleBadge {
  badge_id: string;
  badge_name: string;
  type: BadgeType;
  category: string;
  description: string;
  repeatable: boolean;
}

/** Per-module breakdown of how many times a badge was earned in each module. */
export interface ModuleAward {
  module_id: string;
  module_name: string;
  awarded_count: number;
}

/** A badge the student has actually been awarded. */
export interface AwardedBadge {
  badge_id: string;
  badge_name: string;
  type: BadgeType;
  category: string;
  celebrated: boolean;
  /** Total times earned across every module. */
  awarded_count: number;
  /** Unix seconds. */
  awarded_on: number;
  awarded_by: string;
  /** Primary/latest module the badge was earned in. Empty string = not module-bound. */
  module_id: string;
  /**
   * Every module the badge was earned in, with the per-module count. A
   * repeatable badge can be earned across multiple modules. Optional because
   * older payloads / non-module badges may omit it — callers fall back to
   * `module_id` when this is absent.
   */
  module_awards?: ModuleAward[];
}

export interface BadgesResponse {
  eligible_badges: EligibleBadge[];
  awarded_badges: AwardedBadge[];
}

/* -------------------------- Badge descriptions -------------------------- */
export { BADGE_DESCRIPTIONS } from './badge-descriptions';

/* --------------------------- Local artwork ------------------------------ */
/**
 * Badge artwork, keyed by snake_case `badge_id` (the API's `icon_url` is empty).
 * Note `all_in_avenger` matches the API id spelling for All-In Avenger.
 */
export const BADGE_IMAGES: Record<string, string> = {
  // System / auto badges
  first_step: frame21,
  strong_start: frame11,
  streak_warrior: frame12,
  streak_champion: frame13,
  streak_legend: frame14,
  unstoppable: frame15,
  on_time_star: frame20,
  giant_jump: frame23,
  ace_learner: frame17,
  perfect_score: frame18,
  quiz_practice_star: practiceStar,
  quiz_practice_champion: practiceChampion,
  quiz_practice_legend: practiceLegend,
  quiz_module_attendance_star: moduleAttendanceStar,
  quiz_mq_striker: moduleQuizStriker,
  quiz_mq_star: moduleQuizStar,
  quiz_mq_master: moduleQuizMaster,
  quiz_module_expert: moduleExpert,
  quiz_champion: quizChampion,
  quiz_legend: quizLegend,
  quiz_go_getter: goGetter,
  quiz_big_leap: bigLeap,
  // In-class (≤ 3rd grade)
  super_simba: frame1,
  kind_koala: frame2,
  einstein_spark: frame3,
  ryan_jr: frame8,
  active_ninja: frame9,
  gentle_giraffe: frame4,
  joyful_jupiter: frame5,
  brainy_bee: frame6,
  dolphin_memory: frame7,
  // In-class (4th+)
  iron_man: frame24,
  captain_kind: frame32,
  flash_thinker: frame30,
  curious_galileo: frame26,
  all_in_avenger: frame27,
  zen_master: frame25,
  joy_blaster: frame29,
  curie_precision: frame31,
  memory_panther: frame28,
  math_narrator: frame33,
  bounce_back_boss: frame34,
  // Special recognition (all grades)
  rising_star: frame10,
  sound_hero: soundHero,
  sound_scout: soundScout,
  book_boss: bookBoss,
  sentence_superstar: sentenceSuperstar,
  busy_bee: busyBee,
  brave_bear: braveBear,
  listening_lion: listeningLion,
  clear_voice_captain: clearVoiceCaptain,
  kind_heart: kindHeart,
  fearless_falcon: fearlessFalcon,
  reading_ranger: readingRanger,
  confidence_captain: confidenceCaptain,
  word_wizard: wordWizard,
  curious_cheetah: curiousCheetah,
  power_participant: powerParticipant,
  idea_inventor: ideaInventor,
  rising_rocket: risingRocket,
  word_warrior: wordWarrior,
  knowledge_contributor: knowledgeContributor,
  expression_expert: expressionExpert,
  confidence_communicator: confidenceCommunicator,
  discussion_builder: discussionBuilder,
  curiosity_catalyst: curiosityCatalyst,
  homework_hero: homeworkHero,
  resilience_rockstar: resilienceRockstar,
  reasoning_ranger: reasoningRanger,
  insight_investigator: insightInvestigator,
  bridge_builder: bridgeBuilder,
  leadership_catalyst: leadershipCatalyst,
  perspective_shifter: perspectiveShifter,
  critical_thinker: criticalThinker,
  question_architect: questionArchitect,
  engagement_champion: engagementChampion,
  vocabulary_virtuoso: vocabularyVirtuoso,
  solution_strategist: solutionStrategist,
};

/* ------------------------------- UI types ------------------------------- */
export type BadgeFilter = 'all' | 'module-level';

export interface ModuleInfo {
  id: string;
  title: string;
}

/** One module a badge was earned in, with that module's own earned count. */
export interface BadgeModuleAward {
  moduleId: string;
  /** Readable module name (from the API, with modules-API fallback). */
  moduleName?: string;
  /** Numeric position of the module (e.g. "5" for Module 5), from the modules API. */
  moduleNumber?: string;
  /** Times the badge was earned in THIS module — drives the per-module red dot. */
  count: number;
}

/** Friendly badge shape consumed by the card/drawer UI. */
export interface DrawerBadge {
  badgeId: string;
  name: string;
  imageUrl: string;
  description: string;
  category: string;
  type: BadgeType;
  /** Total times earned across all modules (`awarded_count`). 0 when locked. */
  count: number;
  isLocked: boolean;
  /**
   * Every module this badge was earned in (each with its own count). Empty when
   * the badge is locked or not module-bound.
   */
  moduleAwards: BadgeModuleAward[];
  /**
   * The primary/latest module the badge was earned in (the API's badge-level
   * `module_id`). `awardedOn` belongs to THIS module, so module recency is
   * attributed here only — not to every module in `moduleAwards`. Empty when the
   * badge is locked or not module-bound.
   */
  primaryModuleId?: string;
  /** When the badge was last awarded (unix seconds) — used to order modules. */
  awardedOn?: number;
}

/** Minimal badge shape for the dashboard "My Achievements" preview strip. */
export interface AchievementBadge {
  imageUrl: string;
  name: string;
  isLocked: boolean;
}
